import { c as _c } from "react/compiler-runtime";
import type { ClassificationCategory } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
const CREDENTIALS_CATEGORY = 'CREDENTIALS';

// The API always advertises CREDENTIALS, while scanners only assign it to projects the rollout
// flag is on for, so the flag decides whether it is offered as a selectable class.
export const selectableCategories = (categories: ClassificationCategory[], credentialsEnabled: boolean): ClassificationCategory[] => credentialsEnabled ? categories : categories.filter(c => c.name !== CREDENTIALS_CATEGORY);

// The infected-data dashboard enumerates every DataClassification enum value, so CREDENTIALS shows
// up there (at zero bytes) even for projects the rollout flag never touched.
export const useIsDataClassRolledOut = () => {
  const $ = _c(2);
  const {
    DataClassificationCredentialsEnabled
  } = useFeatureFlags();
  let t0;
  if ($[0] !== DataClassificationCredentialsEnabled) {
    t0 = name => name !== CREDENTIALS_CATEGORY || DataClassificationCredentialsEnabled === true;
    $[0] = DataClassificationCredentialsEnabled;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const useClassificationCategories = () => {
  const dal = useDAL();
  const {
    DataClassificationCredentialsEnabled
  } = useFeatureFlags();
  const {
    data,
    isLoading
  } = dal.inventory.dataClasses.useCategories();
  const allCategories = useMemo<ClassificationCategory[]>(() => data?.categories ?? [], [data?.categories]);
  const categories = useMemo<ClassificationCategory[]>(() => selectableCategories(allCategories, DataClassificationCredentialsEnabled === true), [allCategories, DataClassificationCredentialsEnabled]);

  // Resources classified before the flag was turned off still render their class, so the lookup
  // keeps every category the API returned.
  const lookup = useMemo<Record<string, ClassificationCategory>>(() => Object.fromEntries(allCategories.map(c => [c.name, c])), [allCategories]);
  return {
    categories,
    lookup,
    isLoading
  };
};
export const useDataClassFilterOptions = () => {
  const $ = _c(5);
  const {
    categories,
    isLoading
  } = useClassificationCategories();
  let t0;
  if ($[0] !== categories) {
    t0 = categories.map(_temp);
    $[0] = categories;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const options = t0;
  let t1;
  if ($[2] !== isLoading || $[3] !== options) {
    t1 = {
      options,
      isLoading
    };
    $[2] = isLoading;
    $[3] = options;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
function _temp(c) {
  return {
    id: c.name,
    name: c.name,
    description: c.description
  };
}